// src/pages/Stock.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  startAt,
  endAt,
  where,
} from "firebase/firestore";
import { db } from "../services/firebase";

const DEFAULT_LOCATION_ID = "boutique";
const stockKey = (locId, itemId) => `${locId}__${itemId}`;

// function rid() {
//   return (crypto?.randomUUID && crypto.randomUUID()) || Math.random().toString(36).slice(2);
// }

export default function Stock() {
  const [locationId, setLocationId] = useState(DEFAULT_LOCATION_ID);

  // saisies
  const [term, setTerm] = useState("");       // code-barres ou texte
  const [qty, setQty] = useState("");         // quantité comptée (absolue)
  const [message, setMessage] = useState("");

  // produit courant sélectionné
  const [item, setItem] = useState(null);     // {id, name, barcode, price, cost}
  const [currentQty, setCurrentQty] = useState(null); // qty en stock (location)

  // suggestions (par nom)
  const [sugs, setSugs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);

  const inputRef = useRef(null);

  // Charger la quantité actuelle quand l'item change
  useEffect(() => {
    (async () => {
      if (!item?.id) {
        setCurrentQty(null);
        return;
      }
      const sRef = doc(db, "stock", stockKey(locationId, item.id));
      const snap = await getDoc(sRef);
      setCurrentQty((snap.exists() ? snap.data().qty : 0) || 0);
    })();
  }, [item?.id, locationId]);

  // --- Recherche principale (Enter) ---
  async function goSearch() {
    setMessage("");
    const t = term.trim();
    if (!t) return;

    setLoading(true);
    try {
      // 1) Essai code-barres exact
      const byBarcode = query(
        collection(db, "itemsEK"),
        where("barcode", "==", t),
        limit(1)
      );
      const bSnap = await getDocs(byBarcode);
      if (!bSnap.empty) {
        const d = bSnap.docs[0];
        const it = d.data();
        setItem({
          id: d.id,
          name: it.name || it.title || "Article",
          barcode: it.barcode || "",
          price: Number(it.price || 0),
          cost: Number(it.cost || 0),
        });
        setSugs([]);
        return;
      }

      // 2) Sinon, on affiche des suggestions par nom (préfixe)
      const lower = t.toLowerCase();
      const byName = query(
        collection(db, "itemsEK"),
        orderBy("nameLower"),
        startAt(lower),
        endAt(lower + "\uf8ff"),
        limit(10)
      );
      const nSnap = await getDocs(byName);
      const arr = [];
      nSnap.forEach((d) => {
        const it = d.data();
        arr.push({
          id: d.id,
          name: it.name || it.title || "Article",
          barcode: it.barcode || "",
          price: Number(it.price || 0),
          cost: Number(it.cost || 0),
        });
      });
      setSugs(arr);
      if (!arr.length) setMessage("Aucun résultat. Vérifie le code-barres ou le nom.");
    } catch (e) {
      console.error(e);
      setMessage("Erreur de recherche.");
    } finally {
      setLoading(false);
    }
  }

  // Choisir une suggestion
  function pickSuggestion(s) {
    setItem(s);
    setSugs([]);
    setTerm(s.name);
    // qty courante sera chargée via useEffect
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  // --- Scan caméra (BarcodeDetector) ---
  async function startScan() {
    setMessage("");
    if (!("BarcodeDetector" in window)) {
      setMessage("Scanner non supporté sur ce navigateur.");
      return;
    }
    try {
      const detector = new window.BarcodeDetector({
        formats: ["ean_13", "code_128", "upc_a", "upc_e"],
      });
      setScanning(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();
      const canvas = document.createElement("canvas");
      canvas.width = 640;
      canvas.height = 480;
      const ctx = canvas.getContext("2d");

      const tryDetect = async () => {
        if (!scanning) return;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const bitmap = await createImageBitmap(canvas);
        const codes = await detector.detect(bitmap);
        if (codes.length) {
          const code = codes[0].rawValue;
          stream.getTracks().forEach((t) => t.stop());
          setScanning(false);
          setTerm(code);
          await goSearch(); // recherchera par CB
        } else {
          requestAnimationFrame(tryDetect);
        }
      };
      requestAnimationFrame(tryDetect);
    } catch (e) {
      console.error(e);
      setScanning(false);
      setMessage("Impossible d’ouvrir la caméra.");
    }
  }

  // --- Ajustement absolu (inventaire) ---
  async function adjustAbsolute() {
    setMessage("");
    if (!item?.id) {
      setMessage("Choisis d’abord un article.");
      return;
    }
    const newQty = Number(qty);
    if (!Number.isFinite(newQty) || newQty < 0) {
      setMessage("Quantité invalide.");
      return;
    }

    const sRef = doc(db, "stock", stockKey(locationId, item.id));
    const movRef = doc(collection(db, "inventoryMoves"));

    try {
      await runTransaction(db, async (tx) => {
        // READS
        const sSnap = await tx.get(sRef);
        const oldQty = (sSnap.exists() ? sSnap.data().qty : 0) || 0;
        const delta = newQty - oldQty;

        // WRITES
        tx.set(
          sRef,
          {
            locationId,
            itemId: item.id,
            qty: newQty,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        // trace du mouvement
        tx.set(movRef, {
          type: "adjust",
          locationId,
          itemId: item.id,
          delta,             // +/- selon inventaire
          countedQty: newQty,
          at: serverTimestamp(),
          note: "inventory count",
        });
      });

      setCurrentQty(newQty);
      setMessage(
        `✅ Inventaire ajusté: ${item.name} → ${newQty.toLocaleString()} (loc: ${locationId}).`
      );
      setQty("");
    } catch (e) {
      console.error(e);
      setMessage("Erreur d'ajustement: " + e.message);
    }
  }

  // helpers UI
  const lineInfo = useMemo(() => {
    if (!item) return null;
    return {
      title: item.name || "Article",
      barcode: item.barcode || "—",
      price: Number(item.price || 0),
      cost: Number(item.cost || 0),
    };
  }, [item]);

  function handleKey(e) {
    if (e.key === "Enter") goSearch();
  }

  return (
    <div className="container">
      <h3 className="mb-3">Inventaire / Ajustement</h3>

      <div className="card mb-3">
        <div className="card-body row g-3">
          <div className="col-12 col-md-3">
            <label className="form-label">Emplacement</label>
            <input
              className="form-control"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              placeholder="ex: boutique"
            />
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label">Code-barres ou Nom</label>
            <div className="d-flex gap-2 position-relative">
              <input
                ref={inputRef}
                className="form-control"
                placeholder="Scanner ou taper un nom…"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onKeyDown={handleKey}
                autoComplete="off"
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={goSearch}
                disabled={loading}
              >
                {loading ? "…" : "Chercher"}
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={startScan}
                disabled={scanning}
                title="Scanner via caméra"
              >
                {scanning ? "Scan…" : "Scanner"}
              </button>

              {/* suggestions */}
              {sugs.length > 0 && (
                <div
                  className="list-group position-absolute w-100 mt-5 shadow"
                  style={{ zIndex: 5, maxHeight: 260, overflowY: "auto" }}
                >
                  {sugs.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className="list-group-item list-group-item-action"
                      onClick={() => pickSuggestion(s)}
                    >
                      <div className="fw-semibold">{s.name}</div>
                      <small className="text-muted">
                        CB: {s.barcode || "—"} · Prix: {s.price?.toLocaleString?.() || 0} · Coût:{" "}
                        {s.cost?.toLocaleString?.() || 0}
                      </small>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="form-text">
              Astuce: la recherche « nom » utilise <code>nameLower</code> (préfixe).
            </div>
          </div>

          <div className="col-12 col-md-3">
            <label className="form-label">Quantité comptée</label>
            <input
              type="number"
              className="form-control"
              min="0"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              placeholder="ex: 12"
            />
          </div>
        </div>
      </div>

      {/* Carte produit courant + action */}
      <div className="card">
        <div className="card-body">
          {!item ? (
            <div className="text-muted">Aucun article sélectionné.</div>
          ) : (
            <>
              <div className="d-flex flex-wrap justify-content-between align-items-start">
                <div>
                  <h5 className="mb-1">{lineInfo?.title}</h5>
                  <div className="small text-muted">
                    ID: <code>{item.id}</code> · CB: {lineInfo?.barcode} · Prix:{" "}
                    {lineInfo?.price?.toLocaleString?.() || 0} XOF · Coût:{" "}
                    {lineInfo?.cost?.toLocaleString?.() || 0} XOF
                  </div>
                </div>
                <div className="text-end">
                  <div className="small text-muted">Quantité actuelle ( {locationId} )</div>
                  <div className="fs-5 fw-semibold">
                    {currentQty === null ? "—" : Number(currentQty).toLocaleString()}
                  </div>
                </div>
              </div>

              <hr />

              <div className="d-flex justify-content-between align-items-center">
                <div className="small text-muted">{message || "—"}</div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setItem(null);
                      setTerm("");
                      setQty("");
                      setCurrentQty(null);
                      setSugs([]);
                      setMessage("");
                      inputRef.current?.focus();
                    }}
                  >
                    Réinitialiser
                  </button>
                  <button
                    type="button"
                    className="btn btn-dark"
                    onClick={adjustAbsolute}
                    disabled={!item || qty === "" || Number(qty) < 0}
                    title="Enregistrer la quantité comptée pour cet article"
                  >
                    Enregistrer l’inventaire
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <p className="text-muted small mt-3">
        L’ajustement enregistre un mouvement <code>inventoryMoves</code> avec{" "}
        <code>type="adjust"</code> et <code>delta</code> (différence entre quantité comptée et
        quantité actuelle). Les ventes/réceptions futures continueront à mettre à jour{" "}
        <code>stock/*</code> normalement.
      </p>
    </div>
  );
}
